/**
 * The wear layer — things WORN (D143) and things WORN OUT (D152).
 *
 * @remarks
 * Both are facts about a carried thing rather than about the room it came
 * from: what is on the player's face, and how many pulls the strap has left.
 * They left `items.ts` together at the 500-line cap, and they belong
 * together — the mask is the case that exercises both in one object.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { effectSchema } from './effects.js';
import { factScopeSchema, nonBlankStringSchema, positiveIntegerSchema } from './primitives.js';
import { narrativeLineSchema } from './transcript.js';

/**
 * D143 — see {@link itemDefinitionSchema}'s `equippable`. Every narrative is
 * optional; absent, the engine speaks its slot (`equipped`, `unequipped`,
 * `alreadyWorn`, `slotOccupied`). `slot` names one of the pack's declared
 * `equipmentSlots` (validated at load — a typo would silently mint a slot).
 */
export const equippableSchema = z
	.strictObject({
		alreadyEquippedNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		equipNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/** Effects applied after the equip lines — the mask's `cancel_event`. */
		onEquip: z.array(effectSchema).readonly().default([]),
		onUnequip: z.array(effectSchema).readonly().default([]),
		slot: nonBlankStringSchema,
		slotOccupiedNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		unequipNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
	})
	.readonly();

/** An item's wear/wield declaration — see {@link equippableSchema}. */
export type Equippable = z.infer<typeof equippableSchema>;

/**
 * D152 — the ACTS a thing can be spent by or refuse once spent, named as the
 * engine's own actions rather than as the player's words: by the time a
 * command reaches an item, `remove` has already become a `take` or an
 * `unequip` (D151), `open` a `use` or an `open_container` (D50). The word
 * proposes; the act is what the world counts. `use` spends the INSTRUMENT
 * — the knife wears out, not the rope it cuts.
 */
export const itemActSchema = z.enum([
	'close_container',
	'drop',
	'equip',
	'inspect',
	'open_container',
	'search',
	'switch_off',
	'switch_on',
	'take',
	'unequip',
	'use',
]);

/** One act on an item — see {@link itemActSchema}. */
export type ItemAct = z.infer<typeof itemActSchema>;

/**
 * D152 (Mike: "an equippable item configurable to `{n} times usable`; the
 * mask falls apart when you remove it; a knife breaks when you use it") —
 * a counter on the item, spent by named acts, with an authored crossing.
 *
 * @remarks
 * `uses` acts from `spentBy` spend the thing; the act that crosses zero
 * speaks `spentNarratives` (absent, silence — the crossing is often the
 * verb's own line) and applies `onSpent`, once. What HAPPENS to the object
 * is effects, not an enum: `remove_item` destroys it, a flag leaves a broken
 * thing in the hands — the effects vocabulary already says both, and a
 * `fate` field would be a second way to spell one of them (Calder's §2).
 *
 * A spent thing REFUSES the acts in `refuses` — defaulting to `spentBy`,
 * because a lighter that is spent by flicking refuses flicking — with
 * `spentRefusalNarratives` or the `itemSpent` slot. The mask is the case
 * where the two differ: spent by `unequip`, it refuses `equip`.
 *
 * `scope` is whose fact the wear is (Mike, 2026-09-07: a pack declaration,
 * not an engine opinion): `world` goes back with the checkpoint — die, and
 * the strap is whole again at the photograph; `player` survives it the way
 * the hands do. Default `world`, the gentler reading and the demo's.
 *
 * Not equipment-specific: a keycard with three swipes and a lighter with a
 * dozen flicks want it and neither has a slot.
 */
export const durabilitySchema = z
	.strictObject({
		onSpent: z.array(effectSchema).readonly().default([]),
		refuses: z.array(itemActSchema).min(1).readonly().optional(),
		scope: factScopeSchema.default('world'),
		spentBy: z.array(itemActSchema).min(1).readonly(),
		spentNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		spentRefusalNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		uses: positiveIntegerSchema,
	})
	.readonly();

/** An item's wear counter — see {@link durabilitySchema}. */
export type Durability = z.infer<typeof durabilitySchema>;

/** The acts a spent thing refuses: its own list, else the acts that spent it. */
export const refusedActsOf = (durability: Durability): readonly ItemAct[] => durability.refuses ?? durability.spentBy;
